import { useState, useRef, useEffect, useCallback } from 'react';

/**
 * Custom hook to handle polling with countdown
 * @param {Function} pollingFunction - The function to call when polling
 * @param {number} interval - Polling interval in seconds
 * @param {boolean} shouldPoll - Whether polling should be active
 * @param {Array} dependencies - Additional dependencies for the polling function
 * @returns {Object} - Polling state and controls
 */
const usePolling = (pollingFunction, interval = 30, shouldPoll = true, dependencies = []) => {
  // State for countdown display
  const [countdown, setCountdown] = useState(interval);
  
  // Refs to track polling state outside of render cycle
  const isPollingRef = useRef(false);
  const timerRef = useRef(null);
  const countdownTimerRef = useRef(null);
  const countdownValueRef = useRef(interval);
  
  // Memoize the polling function to avoid recreating it
  const poll = useCallback(async () => {
    // Prevent multiple simultaneous polls
    if (isPollingRef.current) return;
    
    try {
      isPollingRef.current = true;
      await pollingFunction();
    } catch (error) {
      console.error('Polling error:', error);
    } finally {
      isPollingRef.current = false;
    }
  }, [pollingFunction, ...dependencies]);
  
  // Manual refresh function
  const refresh = useCallback(() => {
    if (!isPollingRef.current) {
      poll();
      countdownValueRef.current = interval;
      setCountdown(interval);
    }
  }, [poll, interval]);
  
  // Setup polling and countdown
  useEffect(() => {
    // Initial poll
    if (shouldPoll) {
      poll();
    }
    
    // Setup polling interval
    timerRef.current = setInterval(() => {
      if (shouldPoll && !isPollingRef.current) {
        poll();
        countdownValueRef.current = interval;
      }
    }, interval * 1000);
    
    // Setup countdown timer
    countdownTimerRef.current = setInterval(() => {
      if (shouldPoll) {
        countdownValueRef.current -= 1;
        
        // Only update state if value is different to minimize renders
        if (countdownValueRef.current >= 0) {
          setCountdown(countdownValueRef.current);
        }
        
        // Reset when reaching zero
        if (countdownValueRef.current <= 0) {
          countdownValueRef.current = interval;
        }
      }
    }, 1000);
    
    // Cleanup
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
    };
  }, [poll, interval, shouldPoll]);
  
  return {
    countdown,
    isPolling: isPollingRef.current,
    refresh
  };
};

export default usePolling;
